<template>
  <div class="setting-input-question" :class="[isSubquestion ? 'setting-subquestion' : '']">
     <InfoContainer :message="infoMessage" :state="resolvedNoticeState" :action_label="infoMessage ? resolvedActionLabel : ''" @action="onNoticeAction">
    <div class="w-full">
      <QuestionTitle :question="question" :description="disableNotes" :disabled="disabled || salesChange" :required="required" />
    </div>
    <div v-if="description">
      <div class="mt-1 setting-question-description">{{ description }}</div>
    </div>
   
      <div class="relative">
        <TextInput
          v-model="value"
          :placeholder="placeholder ? placeholder : ' '"
          :disabled="salesChange || disabled || (disableNotes != null && disableNotes != undefined && disableNotes != '')"
          :type="inputType"
          :size="type === 'small' ? 'small' : 'medium'"
          :clearable="false"
          @blur="onBlur"
        >
          <template v-if="prefixIcon || moneyInput" #prefix>
            <img v-if="prefixIcon" :src="prefixIcon" />
            <div v-if="moneyInput" class="setting-dollar-sign">
              {{ currencySymbol }}
            </div>
          </template>
          <!-- In-field state badge ("Validating ...", "Valid NPI", "Prefilled").
               Sits inside the field on the right per the NPI Validation design. -->
          <template v-if="statusBadge" #suffix>
            <span class="question-status-badge" :class="`question-status-badge--${statusBadge.type}`">
              <img :src="statusBadgeIcon" alt="" />
              {{ statusBadge.text }}
            </span>
          </template>
        </TextInput>
      </div>
    </InfoContainer>

    <Notification v-if="successMsg" theme="info" class="mt-0.5" :message="$t(`SettingPage.${successMsg}`)" />
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, onMounted, watch, nextTick } from 'vue';
import QuestionTitle from './QuestionTitle.vue';
import useUtilities from '../service/UseUtilities';
import useEmailDomainCheck from '../service/useEmailDomainCheck';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const { t, te } = useI18n();

const { validateEmail } = useUtilities();

const props = defineProps({
  question: String,
  modelValue: String,
  required: {
    type: Boolean,
    default: false,
  },
  salesChange: {
    type: Boolean,
    default: false,
  },
  moneyInput: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    defualt: '',
  },
  placeholder: {
    type: String,
    defualt: '',
  },
  showSalesChange: {
    type: Boolean,
    default: true,
  },
  inputType: {
    type: String,
    default: 'text',
  },
  type: {
    type: String,
    default: 'medium', // normal, small
  },
  description: {
    type: String,
  },
  disableNotes: {
    type: String,
  },
  min: {
    type: Number,
  },
  max: {
    type: Number,
  },
  pattern: {
    type: Array,
  },
  patternErrorMsg: {
    type: Array,
  },
  warningMsg: {
    type: String,
    default: '',
  },
  // VP-18720: notice box state for warningMsg ('error' | 'alert') and an
  // optional clickable action shown right after it (emits `action`)
  noticeState: {
    type: String,
    default: 'error',
  },
  actionLabel: {
    type: String,
    default: '',
  },
  // VP-18720: every email field runs the signup form's domain check on blur
  // (TLD + DNS, typo suggestion in the notice, blocks while invalid). Forms that
  // drive the check themselves (login email) pass false and use
  // warningMsg / noticeState / actionLabel instead.
  domainCheck: {
    type: Boolean,
    default: true,
  },
  showOptional: {
    defalut: false,
  },
  optionalWording: {
    default: 'optional',
  },
  info: {
    type: String,
  },
  prefix: {
    type: String,
    default: null,
  },
  prefixIcon: {
    type: String,
    default: null,
  },
  isSubquestion: {
    type: Boolean,
    default: false,
  },
  // Optional in-field badge: { type: 'validating' | 'valid' | 'prefilled', text }.
  // Null renders nothing, so every existing caller is unaffected.
  statusBadge: {
    type: Object,
    default: null,
  },
});

// The shared checked-greenstroke.svg is teal (#25B8C2); the NPI Validation
// design calls for Success Text Green (#45960D), hence a dedicated asset.
const STATUS_BADGE_ICONS = {
  validating: require('@/assets/MyPractice/npi-validating.svg'),
  valid: require('@/assets/MyPractice/check-circle-success.svg'),
  prefilled: require('@/assets/MyPractice/check-circle-success.svg'),
};
const statusBadgeIcon = computed(() => STATUS_BADGE_ICONS[props.statusBadge?.type]);

onMounted(() => {
  validateInput(props.modelValue);
});

const store = useStore();
const currencySymbol = computed(() => store.getters['myPractice/billing/currencySymbol']);

const emit = defineEmits(['update:modelValue', 'validateInput', 'blur', 'action']);

const isEmailQuestion = computed(() => !!props.question?.toLowerCase().includes('email'));
const domainCheckEnabled = computed(
  () => props.domainCheck && isEmailQuestion.value && !props.disabled && !props.salesChange,
);
const emailDomain = useEmailDomainCheck(() => (domainCheckEnabled.value ? props.modelValue : ''));
// re-validate once the check settles so the parent's error flag (and Save) follow it
const runEmailDomainCheck = () => emailDomain.check().then(() => validateInput(props.modelValue));

const onBlur = () => {
  hasInteracted.value = true;
  emit('blur');
  if (domainCheckEnabled.value) runEmailDomainCheck();
};

// The parent's own message (e.g. an NPI the backend rejected) wins over the
// built-in domain notice; the format error comes from validateInput as before.
const parentNoticeActive = computed(() => !!(props.error && props.warningMsg));
const builtInEmailNotice = computed(() =>
  (domainCheckEnabled.value && !parentNoticeActive.value ? emailDomain.notice.value : null));
const resolvedNoticeState = computed(() =>
  (parentNoticeActive.value ? props.noticeState : builtInEmailNotice.value?.state || 'error'));
const resolvedActionLabel = computed(() =>
  (parentNoticeActive.value ? props.actionLabel : builtInEmailNotice.value?.action || ''));
const onNoticeAction = async () => {
  if (parentNoticeActive.value || !builtInEmailNotice.value) {
    emit('action');
    return;
  }
  // clicking the suggestion replaces the email and checks the new one
  emit('update:modelValue', builtInEmailNotice.value.action);
  await nextTick();
  runEmailDomainCheck();
};
const errorMsg = ref('');
const successMsg = ref('');
// Whether the user has typed in or left this field. Validation itself still
// runs (and is reported upwards) from mount on, so parents keep gating Save on
// it - this only decides whether the message is shown, so a form that opens
// with empty required fields doesn't greet the user with errors.
const hasInteracted = ref(false);
const value = computed({
  get: () => {
    return props.modelValue;
  },
  set: (val) => {
    if (props.inputType === 'number') val = String(val);
    // Email fields only: the message waits for blur, so typing the first
    // characters of an address never shows "invalid" (VP-18720). Every other
    // field keeps showing its message while typing, as before. Validation
    // itself still runs on every change for the parent's Save gate.
    if (!isEmailQuestion.value) hasInteracted.value = true;
    emit('update:modelValue', val);
  },
});

const infoMessage = computed(() => {
  // A message the parent passed in is always deliberate (e.g. an NPI the
  // backend rejected), so it isn't gated on interaction.
  if (props.error && props.warningMsg) {
    return te(`SettingPage.${props.warningMsg}`) ? t(`SettingPage.${props.warningMsg}`) : props.warningMsg;
  }
  if (builtInEmailNotice.value) return builtInEmailNotice.value.message;
  if (props.error && errorMsg.value && hasInteracted.value) {
    return errorMsg.value;
  }
  return '';
});

const validateInput = (val) => {
  let validated = true;
  errorMsg.value = '';
  successMsg.value = '';
  if (props.salesChange || props.disabled) {
    emit('validateInput', validated);
    return validated;
  }
  if (props.required) {
    validated = props.inputType === 'number' ? val != undefined || val != null : val?.trim() != '';
    if (!validated) {
      errorMsg.value = t('SettingPage["Required fields cannot be empty."]');
      emit('validateInput', validated);
      return validated;
    }
  }
  if (props.pattern?.length > 0) {
    let errors = [];
    props.pattern.forEach((p, i) => {
      if (!p.test(val.toLowerCase())) {
        errors.push(props.patternErrorMsg[i]);
      }
    });
    validated = errors.length === 0;
    if (!validated) {
      errorMsg.value = errors[0];
      emit('validateInput', false);
      return validated;
    } else {
      errorMsg.value = '';
    }
  }
  if (props.question?.toLowerCase().includes('email')) {
    validated = val === '' || val === undefined || validateEmail(val);
    if (!validated) errorMsg.value = t('SettingPage.Please enter a valid Email');
    // a domain that cannot receive mail blocks like a format error; its message is the notice above
    if (validated && domainCheckEnabled.value && emailDomain.isInvalid.value) validated = false;
  } else if (props.inputType === 'number') {
    validated = val >= props.min && val <= props.max;
    if (!validated) errorMsg.value = t('SettingPage.Please enter a number between ${min}-${max}', { min: props.min, max: props.max });
  }
  emit('validateInput', validated);
  return validated;
};

watch(
  () => props.modelValue,
  (newVal) => {
    validateInput(newVal);
  },
);
</script>

<style scoped>
/* EHR_BodyS_12_Web: Inter 400, 12/16, letter-spacing 0.4 */
.question-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.question-status-badge img {
  width: 16px;
  height: 16px;
}

/* Success Text Green */
.question-status-badge--valid {
  color: #45960D;
}

/* The icon carries the colour on these two, not the text */
.question-status-badge--prefilled,
.question-status-badge--validating {
  color: var(--TrueBlack, #333);
}

.question-status-badge--validating img {
  animation: question-status-spin 1s linear infinite;
}

@keyframes question-status-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
